import { create } from 'zustand'
import { subscribeWithSelector } from 'zustand/middleware'
import { supabase } from '@/lib/supabase'
import { setSentryUser, clearSentryUser } from '@/lib/sentry'
import { useCallStatusStore } from '@/stores/callStatusStore'
import { deactivateCallStatus } from '@/services/call-status.service'
import type { Profile, AuthResponse, LoginCredentials } from '@/types'
import type {
  UserData,
  Session,
  SimpleLoginResponse,
  GetProfileResponse
} from '@/types/auth.types'

interface AuthState {
  // State (100% do AuthContext)
  user: UserData | null
  session: Session | null
  profile: Profile | null
  loading: boolean
  isAuthenticated: boolean
  redirectAfterLogin: string | null

  // Actions (100% do AuthContext)
  signIn: (credentials: LoginCredentials) => Promise<AuthResponse>
  signOut: () => Promise<void>
  resetPassword: (email: string) => Promise<void>
  updateProfile: (updates: Partial<Profile>) => Promise<void>
  refreshProfile: () => Promise<void>
  initSession: () => void
  setRedirectAfterLogin: (path: string | null) => void
}

export const useAuthStore = create<AuthState>()(
  subscribeWithSelector(
    (set, get) => ({
      user: null,
      session: null,
      profile: null,
      loading: true,
      isAuthenticated: false,
      redirectAfterLogin: null,

      signIn: async (credentials) => {
        try {
          const { email, password } = credentials

          const { data, error } = await (supabase.rpc as unknown as (fn: string, args: Record<string, unknown>) => Promise<{ data: SimpleLoginResponse | null; error: Error | null }>)('simple_login', {
            p_email: email.toLowerCase().trim(),
            p_password: password
          })

          if (error) {
            throw new Error(error.message ?? 'Erro ao fazer login')
          }

          if (!data || !data.success) {
            const errorMessage = data?.error || 'Credenciais inválidas'
            throw new Error(errorMessage)
          }

          const userData = data.user
          if (!userData) {
            throw new Error('Dados do usuário não retornados pelo servidor')
          }

          const profileData = {
            id: userData.id,
            email: userData.email,
            full_name: userData.full_name,
            role: userData.role,
            avatar_url: userData.avatar_url,
            is_active: userData.is_active,
            phone: null,
            certificadora: userData.certificadora || null,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            last_login: new Date().toISOString()
          } as unknown as Profile

          set({
            user: userData,
            profile: profileData,
            session: { user: userData },
            isAuthenticated: true,
          })

          setSentryUser(userData.id, userData.email, userData.full_name)

          const sessionData = {
            user: userData,
            profile: profileData,
            timestamp: new Date().toISOString()
          }
          localStorage.setItem('crm-simple-auth', JSON.stringify(sessionData))

          return {
            success: true,
            message: 'Login realizado com sucesso'
          }
        } catch (err: unknown) {
          const errorMessage = err instanceof Error ? err.message : 'Erro ao fazer login'
          return {
            success: false,
            message: errorMessage
          }
        }
      },

      signOut: async () => {
        const { user } = get()

        if (user?.id) {
          try {
            await deactivateCallStatus(user.id)
          } catch {
            // Error handled silently
          }
        }

        useCallStatusStore.getState().clearAll()
        clearSentryUser()

        set({
          user: null,
          session: null,
          profile: null,
          isAuthenticated: false,
        })

        localStorage.removeItem('crm-simple-auth')
        localStorage.removeItem('crm-auth-storage')
      },

      resetPassword: async (email) => {
        const normalizedEmail = email.toLowerCase().trim()

        const { error } = await supabase.auth.resetPasswordForEmail(normalizedEmail, {
          redirectTo: `${window.location.origin}/reset-password`,
        })

        if (error) {
          throw error
        }
      },

      updateProfile: async (updates) => {
        const { user } = get()
        if (!user) {
          throw new Error('No user logged in')
        }

        const { error } = await supabase
          .from('profiles')
          .update({
            ...updates,
            updated_at: new Date().toISOString()
          })
          .eq('id', user.id)

        if (error) {
          throw error
        }

        await get().refreshProfile()
      },

      refreshProfile: async () => {
        const { user } = get()
        if (!user?.email) return

        try {
          const { data } = await (supabase.rpc as unknown as (fn: string, args: Record<string, unknown>) => Promise<{ data: GetProfileResponse | null; error: Error | null }>)('get_profile_by_email', {
            p_email: user.email
          })

          if (data?.success && data.profile) {
            set({ profile: data.profile })

            const savedSession = localStorage.getItem('crm-simple-auth')
            if (savedSession) {
              const parsed = JSON.parse(savedSession) as { profile?: Profile }
              parsed.profile = data.profile
              localStorage.setItem('crm-simple-auth', JSON.stringify(parsed))
            }
          }
        } catch {
          // Error handled silently
        }
      },

      initSession: () => {
        try {
          const savedSession = localStorage.getItem('crm-simple-auth')

          if (savedSession) {
            const parsed = JSON.parse(savedSession)
            set({
              user: parsed.user,
              profile: parsed.profile,
              session: { user: parsed.user },
              isAuthenticated: true,
              loading: false,
            })
            // Sync role from DB in background — picks up admin role changes without requiring logout
            void get().refreshProfile()
          } else {
            set({ loading: false })
          }
        } catch {
          set({ loading: false })
        }
      },

      setRedirectAfterLogin: (path) => set({ redirectAfterLogin: path }),
    })
  )
)

// Hook de compatibilidade (facilita migracao gradual dos 46 arquivos)
export function useAuth() {
  const store = useAuthStore()
  return store
}
